import { permissionState } from "@app/store/permissionStore.svelte.js";

/*
 * Primary toolbar items an IFrame parent can show or hide. Only menus and
 * items that the offline IFrame build renders are listed; File, Help and CLI
 * menus and online-only items are never shown in the IFrame, whatever the
 * configuration says.
 */
export const PRIMARY_TOOLBAR_MENUS = {
    edit: [
        "edit-undo",
        "edit-redo",
        "edit-cut",
        "edit-copy",
        "edit-paste",
        "edit-remove-empty",
        "edit-remove-sdh",
        "edit-remove-spaces",
        "edit-goto",
    ],
    format: [
        "boldButton",
        "underlineButton",
        "italicsButton",
        "clearFormattingButton",
        "autoFormatButton",
        "snapToShotsButton",
        "snapToEventStartsButton",
        "snapToEventEndsButton",
        "fixEventOverlapButton",
        "orderByTimeButton",
        "convertToPopOnButton",
        "captionStyleButton",
        "captionPositionButton",
        "spellNumbersButton",
        "removeRepeatWordsButton",
        "replaceWithEllipsesButton",
        "splitOnDashButton",
        "fixRtlTextButton",
        "reverseRtlStartAndEndButton",
        "convertToUpperCaseButton",
        "convertToLowerCaseButton",
        "convertToTitleCaseButton",
        "convertToSentenceCaseButton",
    ],
    timecode: [
        "projectSettingsBtn",
        "convertTimecodeBtn",
        "tcOffsetBtn",
        "timeStretchBtn",
        "autoCorrectBtn",
        "copyTcBtn",
        "trimDurationBtn",
        "subtractOneFrameBtn",
        "addOneFrameBtn",
        "clearTcBtn",
    ],
    insert: [
        "insertEventBefore",
        "insertEventAfter",
        "insertBlock",
        "insertEllipsis",
        "insertMusicNotes",
        "insertSpeaker",
        "insertSpeakerColor",
        "insertBlankFrames",
    ],
    aiTools: ["forceRenderAudioBtn", "forceRenderAllAudioBtn"],
    workspaces: [
        "workspace-default",
        "workspace-reverse",
        "workspace-vertical",
        "workspace-ultrawide",
    ],
    language: [
        "langEnBtn",
        "langFrBtn",
        "langDeBtn",
        "langItBtn",
        "langRuBtn",
        "langEsBtn",
        "langZhCnBtn",
        "langJaBtn",
        "langHiBtn",
        "langPtPtBtn",
    ],
    theme: ["lightThemeBtn", "darkThemeBtn", "autoThemeBtn"],
};

export const PRIMARY_TOOLBAR_BUTTONS = ["timeline", "listPreview", "fullscreen"];

/**
 * Discovery data for integrators.
 */
export function getPrimaryToolbarOptions() {
    return {
        menus: Object.fromEntries(
            Object.entries(PRIMARY_TOOLBAR_MENUS).map(([id, items]) => [id, [...items]]),
        ),
        buttons: [...PRIMARY_TOOLBAR_BUTTONS],
    };
}

function normalizeMenus(menus, ignored) {
    if (menus === undefined) {
        return undefined;
    }
    if (!menus || typeof menus !== "object" || Array.isArray(menus)) {
        throw new Error("primaryToolbar.menus must be an object.");
    }

    const normalized = {};
    Object.entries(menus).forEach(([menuId, value]) => {
        const knownItems = PRIMARY_TOOLBAR_MENUS[menuId];
        if (!knownItems) {
            ignored.push(`menus.${menuId}`);
            return;
        }

        if (typeof value === "boolean") {
            normalized[menuId] = value;
            return;
        }

        if (!Array.isArray(value)) {
            ignored.push(`menus.${menuId}`);
            return;
        }

        const items = [];
        value.forEach((itemId) => {
            if (knownItems.includes(itemId) && !items.includes(itemId)) {
                items.push(itemId);
            } else {
                ignored.push(`menus.${menuId}.${itemId}`);
            }
        });
        normalized[menuId] = items.length > 0 ? items : false;
    });

    return normalized;
}

function normalizeButtons(buttons, ignored) {
    if (buttons === undefined) {
        return undefined;
    }
    if (!buttons || typeof buttons !== "object" || Array.isArray(buttons)) {
        throw new Error("primaryToolbar.buttons must be an object.");
    }

    const normalized = {};
    Object.entries(buttons).forEach(([buttonId, value]) => {
        if (!PRIMARY_TOOLBAR_BUTTONS.includes(buttonId) || typeof value !== "boolean") {
            ignored.push(`buttons.${buttonId}`);
            return;
        }
        normalized[buttonId] = value;
    });

    return normalized;
}

/**
 * Validate a primary toolbar configuration.
 * @param {{ menus?: Record<string, boolean | string[]>, buttons?: Record<string, boolean> } | null | undefined} settings
 * @returns {{ config: object | null, ignored: string[] }}
 */
export function normalizePrimaryToolbarConfig(settings) {
    if (settings === null || settings === undefined) {
        return { config: null, ignored: [] };
    }
    if (typeof settings !== "object" || Array.isArray(settings)) {
        throw new Error("Primary toolbar settings must be an object, null, or undefined.");
    }

    const ignored = Object.keys(settings)
        .filter((key) => key !== "menus" && key !== "buttons");
    const menus = normalizeMenus(settings.menus, ignored);
    const buttons = normalizeButtons(settings.buttons, ignored);

    return { config: { menus: menus ?? {}, buttons: buttons ?? {} }, ignored };
}

/**
 * Apply a primary toolbar configuration. Menus left out keep their default
 * contents; `false` hides a menu and an array shows only the listed item ids.
 * Pass null or undefined to restore the default toolbar.
 */
export function configurePrimaryToolbar(settings) {
    const { config, ignored } = normalizePrimaryToolbarConfig(settings);
    permissionState.primaryToolbar = config;
    permissionState.role = "custom";

    return {
        success: true,
        primaryToolbar: config ? JSON.parse(JSON.stringify(config)) : null,
        ignored,
    };
}
